import { Box, VStack } from '@chakra-ui/react'
import { NodeProps, Position } from '@xyflow/react'
import React, { useMemo } from 'react'

import { ScheduleNodeData } from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { ScheduleSelector } from '../core/ScheduleSelector'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useScheduleNode } from './hooks/useScheduleNode'

export interface ScheduleNodeProps extends NodeProps {
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void
  showDeleteButton?: boolean
  // Note: schedules is provided via Jotai atoms (schedulesAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

const ScheduleNodeComponent: React.FC<ScheduleNodeProps> = ({
  id,
  data,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const nodeData = data as ScheduleNodeData & Record<string, unknown>
  const { currentScheduleId, handleScheduleChange, hasData } = useScheduleNode(
    id,
    nodeData,
  )
  const t = useSwitchboardT()

  // Add three custom handles on the right side: Open (top), Closed (middle), Holiday (bottom)
  // ⚠️ Note: Labels are "Open" and "Closed" (NOT "When Open" and "When Closed") per documentation
  // Note: React Flow Position enum doesn't support TopRight/BottomRight, so we use Position.Right
  // with custom styles. The converter uses handle IDs (whenOpen, whenClosed, holiday) to map to
  // legacy anchors (TopRight, Right, BottomRight) - see FIXED_HANDLE_ANCHORS.schedule
  // Order matters - Holiday is last so it renders on top and is clickable
  // Memoize handles array to prevent re-creating on every render
  const scheduleHandles = useMemo(
    () => [
      {
        id: 'whenOpen',
        position: Position.Right as Position, // Mapped to TopRight in legacy via handle ID
        style: {
          top: '0px',
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.schedule.open', 'Open'),
      },
      {
        id: 'whenClosed',
        position: Position.Right as Position, // Mapped to Right in legacy via handle ID
        style: {
          top: 'calc(50%)',
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.schedule.closed', 'Closed'),
      },
      {
        id: 'holiday',
        position: Position.Right as Position, // Mapped to BottomRight in legacy via handle ID
        style: {
          top: 'calc(100%)',
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.schedule.holiday', 'Holiday'),
      },
    ],
    [t],
  )

  return (
    <BaseNode
      label={text(t, 'switchboard.nodeLabels.schedule', 'SCHEDULE')}
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
      sourceHandlePosition={null}
      additionalSourceHandles={scheduleHandles}
    >
      <VStack align="stretch" gap={3}>
        <Box>
          <ScheduleSelector
            value={currentScheduleId}
            onChange={handleScheduleChange}
            placeholder={text(
              t,
              'switchboard.core.selectSchedule',
              'Select schedule...',
            )}
          />
        </Box>
      </VStack>
    </BaseNode>
  )
}

export const ScheduleNode = React.memo(ScheduleNodeComponent, nodePropsAreEqual)
